import useNotice from '@src/hooks/useNotice';
import { devices } from '@src/styles/theme';
import { convertLinksToHyperlinks } from '@src/utils/community';
import Pagination from '@src/components/Molecules/Pagination';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

/** 한 페이지에 보여줄 공지 수 (디자인의 페이지네이션) */
const PAGE_SIZE = 8;

/**
 * 공지사항 (Pple Notice 디자인 이식).
 * 구성: 타이틀+검색 → 중요 공지 배너 → 카테고리 칩 → 아코디언 리스트
 *
 * category/urgent는 백엔드에 새로 추가한 필드로, 값이 없는 기존 공지는 '안내'로 표시한다.
 */
const CATEGORY_LABEL: Record<string, string> = {
  MAINTENANCE: '점검',
  UPDATE: '업데이트',
  INFO: '안내',
  EVENT: '이벤트',
};

const FILTERS = [
  { value: '', label: '전체' },
  { value: 'MAINTENANCE', label: '점검' },
  { value: 'UPDATE', label: '업데이트' },
  { value: 'INFO', label: '안내' },
  { value: 'EVENT', label: '이벤트' },
];

const formatDate = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}.${String(d.getDate()).padStart(2, '0')}`;
};

const NoticeList = () => {
  const { fetchNotice } = useNotice();
  const { data: noticeList } = fetchNotice();
  const [open, setOpen] = useState('');
  const [filter, setFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);

  const notices = useMemo(() => (noticeList as any[]) ?? [], [noticeList]);

  // 중요 공지 중 가장 최근 1건을 상단 배너로
  const banner = useMemo(
    () => notices.find((n) => n?.urgent),
    [notices]
  );

  const visible = useMemo(() => {
    const k = keyword.trim();
    return notices
      .filter((n) => !filter || n?.category === filter)
      .filter((n) => !k || (n?.title ?? '').includes(k));
  }, [notices, filter, keyword]);

  // 필터/검색이 바뀌면 1페이지로 되돌린다.
  useEffect(() => {
    setPage(0);
  }, [filter, keyword]);

  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  const paged = visible.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Wrap>
      <TitleRow>
        <Title>공지사항</Title>
        <SubText>서비스 소식과 점검 안내를 확인하세요</SubText>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            value={keyword}
            placeholder="공지 검색"
            onChange={(e) => setKeyword(e.target.value)}
          />
        </SearchBox>
      </TitleRow>

      {banner && (
        <Banner onClick={() => setOpen(banner.uuid)}>
          <BannerBadge>중요</BannerBadge>
          <BannerText>{banner.title}</BannerText>
          <BannerDate>{formatDate(banner.createdAt)}</BannerDate>
        </Banner>
      )}

      <Filters>
        {FILTERS.map((f) => (
          <Chip
            key={f.label}
            active={filter === f.value}
            onClick={() => {
              setFilter(f.value);
              setOpen('');
            }}
          >
            {f.label}
          </Chip>
        ))}
      </Filters>

      <ListCard>
        {paged.length === 0 && <Empty>공지사항이 없습니다.</Empty>}
        {paged.map((notice) => {
          const isOpen = open === notice.uuid;
          const category = CATEGORY_LABEL[notice?.category] ?? '안내';
          return (
            <Item key={notice.uuid}>
              <Row
                open={isOpen}
                onClick={() =>
                  setOpen((prev) => (prev === notice.uuid ? '' : notice.uuid))
                }
              >
                <TagBadge urgent={!!notice?.urgent}>{category}</TagBadge>
                <RowTitle open={isOpen}>{notice.title}</RowTitle>
                <RowDate>{formatDate(notice.createdAt)}</RowDate>
                <Chevron open={isOpen}>⌄</Chevron>
              </Row>
              {isOpen && (
                <BodyWrap>
                  <Body
                    dangerouslySetInnerHTML={convertLinksToHyperlinks(
                      notice.content
                    )}
                  />
                  <Contact>문의 : help@pple.kr</Contact>
                </BodyWrap>
              )}
            </Item>
          );
        })}
      </ListCard>

      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={(p) => {
          setPage(p);
          setOpen('');
        }}
      />
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 24px;
  }
`;
const SubText = styled.span`
  font-size: 14px;
  color: #767676;
  padding-bottom: 5px;
`;
const SearchBox = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 19px;
  padding: 0 14px;
  height: 38px;
  width: 220px;
  @media ${devices.tablet} {
    margin-left: 0;
    width: 100%;
  }
`;
const SearchIcon = styled.span`
  flex: none;
  width: 14px;
  height: 14px;
  border: 1.6px solid #9a9a9a;
  border-radius: 50%;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 6px;
    height: 1.6px;
    background: #9a9a9a;
    transform: rotate(45deg);
  }
`;
const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Regular', sans-serif;
  font-size: 13.5px;
  color: #222222;
  width: 100%;
`;
const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff1ed;
  border-radius: 16px;
  padding: 18px 22px;
  cursor: pointer;
`;
const BannerBadge = styled.span`
  flex: none;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #ff4651;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
`;
const BannerText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #222222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const BannerDate = styled.span`
  flex: none;
  font-size: 12.5px;
  color: #c0392f;
`;
const Filters = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const Chip = styled.button<{ active: boolean }>`
  height: 36px;
  padding: 0 16px;
  border-radius: 18px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
`;
const ListCard = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  overflow: hidden;
  @media ${devices.tablet} {
    border-radius: 16px;
  }
`;
const Item = styled.div`
  & + & {
    border-top: 1px solid #f2f2f2;
  }
`;
const Row = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  cursor: pointer;
  background: ${({ open }) => (open ? '#FBFBFB' : '#ffffff')};
  @media ${devices.tablet} {
    padding: 16px;
    gap: 8px;
  }
`;
const TagBadge = styled.span<{ urgent: boolean }>`
  flex: none;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  background: ${({ urgent }) => (urgent ? '#FF4651' : '#FFF1ED')};
  color: ${({ urgent }) => (urgent ? '#ffffff' : '#FF4651')};
`;
const RowTitle = styled.span<{ open: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: ${({ open }) => (open ? 700 : 600)};
  color: #222222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const RowDate = styled.span`
  flex: none;
  font-size: 12.5px;
  color: #b7b7b7;
`;
const BodyWrap = styled.div`
  padding: 0 24px 20px;
  background: #fbfbfb;
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media ${devices.tablet} {
    padding: 0 16px 18px;
  }
`;
const Contact = styled.span`
  font-size: 12.5px;
  color: #b7b7b7;
`;
const Body = styled.div`
  font-size: 14.5px;
  line-height: 25px;
  color: #444444;
  white-space: pre-wrap;
  a {
    color: #ff4651;
  }
`;
const Empty = styled.div`
  padding: 60px 0;
  text-align: center;
  font-size: 14px;
  color: #767676;
`;
const Chevron = styled.span<{ open: boolean }>`
  flex: none;
  font-size: 15px;
  line-height: 1;
  color: #b7b7b7;
  transition: transform 0.2s;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
`;

export default NoticeList;
